import React, { useMemo, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { B2BTextField } from "halifax";
import clsx from "clsx";
import { FlightNumberFormConnectorProps } from "./container";
import { AirlineSelect } from "./AirlineSelect";
import { IATA_AIRLINES, POPULAR_AIRLINE_CODES } from "./iataAirlines";
import {
  FLIGHT_NUMBER_TITLE,
  FLIGHT_NUMBER_SUBTITLE,
  FLIGHT_NUMBER_LABEL,
  FLIGHT_NUMBER_ERROR_HELPER,
  ADD_FLIGHT_NUMBER,
} from "./constants";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";
import "./styles.scss";

const OPTIONAL_LABEL = <span className="optional-label">(Optional)</span>;

const AIRLINE_REQUIRED_ERROR = "Select an airline.";
const FLIGHT_NUMBER_REQUIRED_ERROR = "Enter a flight number.";

export interface IFlightNumberFormProps extends FlightNumberFormConnectorProps {
  isMobile?: boolean;
  disabled?: boolean;
}

export const FlightNumberForm = ({
  flightInfo,
  flightInfoFieldSpec,
  setFlightInfo,
  isMobile,
  disabled,
}: IFlightNumberFormProps) => {
  const [airlineTouched, setAirlineTouched] = useState(false);
  const [flightNumberTouched, setFlightNumberTouched] = useState(false);

  const airlineCode = flightInfo?.airlineCode ?? "";
  const flightNumber = flightInfo?.flightNumber ?? "";

  const airlineFilled = airlineCode.trim().length > 0;
  const flightNumberFilled = flightNumber.trim().length > 0;

  // The airline value comes from a controlled dropdown, so it is always a valid
  // 2-char alphanumeric IATA code — we deliberately do NOT apply the shop
  // schema's `airline-code` pattern (`^[A-Z]{2}$`, letters-only), which would
  // wrongly reject real carriers like B6 (JetBlue) / F9 (Frontier). Only the
  // free-text flight number is pattern-validated (schema `^[0-9]{2,4}$`).
  const flightNumberPattern = flightInfoFieldSpec?.flightNumber.pattern;
  const flightNumberPatternInvalid =
    flightNumberFilled &&
    flightNumberPattern != null &&
    !flightNumberPattern.test(flightNumber.trim());

  // Compound field is Optional ⇒ both-empty is allowed; inner fields Required ⇒
  // a partial fill (exactly one of the two) blocks continuing.
  const airlineError = airlineTouched && !airlineFilled && flightNumberFilled;
  const flightNumberError =
    flightNumberTouched &&
    ((airlineFilled && !flightNumberFilled) || flightNumberPatternInvalid);

  const flightNumberErrorHelper = useMemo(() => {
    if (!flightNumberError) return undefined;
    if (airlineFilled && !flightNumberFilled) return FLIGHT_NUMBER_REQUIRED_ERROR;
    return FLIGHT_NUMBER_ERROR_HELPER;
  }, [flightNumberError, airlineFilled, flightNumberFilled]);

  const onAirlineSelect = (code: string) => {
    setAirlineTouched(true);
    setFlightNumberTouched(true);
    setFlightInfo({ airlineCode: code });
    trackEvent({ eventName: ADD_FLIGHT_NUMBER, properties: {} });
  };

  const onFlightNumberChange = (value: string) => {
    setFlightInfo({ flightNumber: value });
    trackEvent({ eventName: ADD_FLIGHT_NUMBER, properties: {} });
  };

  return (
    <Box className={clsx("flight-number-form", { mobile: isMobile })}>
      <Box className="flight-number-form-description">
        <Typography component="h3" variant="h4" className="title">
          {FLIGHT_NUMBER_TITLE} {OPTIONAL_LABEL}
        </Typography>
        <Typography variant="body2" className="subtitle">
          {FLIGHT_NUMBER_SUBTITLE}
        </Typography>
      </Box>

      <Box className="flight-number-form-fields">
        <Box className="airline-select-wrapper">
          <AirlineSelect
            airlines={IATA_AIRLINES}
            popularCodes={POPULAR_AIRLINE_CODES}
            value={airlineCode || undefined}
            onSelect={onAirlineSelect}
            isMobile={isMobile}
            disabled={disabled}
            id="flight-airline-select"
          />
          {airlineError && (
            <Typography variant="caption" className="airline-select-error">
              {AIRLINE_REQUIRED_ERROR}
            </Typography>
          )}
        </Box>

        <B2BTextField
          className="flight-number-input-field"
          label={FLIGHT_NUMBER_LABEL}
          value={flightNumber}
          onChange={onFlightNumberChange}
          onBlur={() => setFlightNumberTouched(true)}
          type="text"
          id="flight-number-input-field"
          errorHelper={flightNumberErrorHelper}
          disabled={disabled}
          fullWidth
        />
      </Box>
    </Box>
  );
};
